import { useState, useEffect, useCallback, memo, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Sun, Moon, Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/hooks/useTheme';
import { searchTools, Tool } from '@/lib/tools-data';
import { cn } from '@/lib/utils';

interface HeaderProps {
  onMenuToggle?: () => void;
  isMobileMenuOpen?: boolean;
}

// Memoized search result item
const SearchResultItem = memo(function SearchResultItem({ 
  tool, 
  onClick 
}: { tool: Tool; onClick: () => void }) {
  const Icon = tool.icon;
  return (
    <button
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm hover:bg-accent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      role="option"
      aria-selected="false"
    >
      <Icon className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
      <div>
        <p className="font-medium">{tool.name}</p>
        <p className="text-xs text-muted-foreground">{tool.description}</p>
      </div>
    </button>
  );
});

export const Header = memo(function Header({ onMenuToggle, isMobileMenuOpen }: HeaderProps) {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [showResults, setShowResults] = useState(false);

  // Debounced search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim()) return [];
    return searchTools(searchQuery);
  }, [searchQuery]);

  const handleToolClick = useCallback((tool: Tool) => {
    navigate(tool.route);
    setSearchQuery('');
    setShowResults(false);
  }, [navigate]);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  }, []);

  const handleSearchFocus = useCallback(() => {
    if (searchQuery) setShowResults(true);
  }, [searchQuery]);

  const handleSearchBlur = useCallback(() => {
    setTimeout(() => setShowResults(false), 200);
  }, []);

  // Show results when search query changes
  useEffect(() => {
    setShowResults(searchQuery.trim().length > 0);
  }, [searchQuery]);

  return (
    <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 px-4 lg:px-6" role="banner">
      {/* Mobile Menu Toggle */}
      <Button
        variant="ghost"
        size="icon"
        className="lg:hidden"
        onClick={onMenuToggle}
        aria-expanded={isMobileMenuOpen}
        aria-controls="mobile-menu"
        aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
      >
        {isMobileMenuOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
      </Button>

      {/* Search */}
      <div className="relative flex-1 max-w-xl" role="search">
        <label htmlFor="search-tools" className="sr-only">Search tools</label>
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground pointer-events-none" aria-hidden="true" />
        <input
          id="search-tools"
          type="search"
          placeholder="Search tools..."
          value={searchQuery}
          onChange={handleSearchChange}
          onFocus={handleSearchFocus}
          onBlur={handleSearchBlur}
          className="search-input"
          aria-autocomplete="list"
          aria-controls="search-results"
          aria-expanded={showResults && searchResults.length > 0}
          autoComplete="off"
        />
        
        {/* Search Results Dropdown */}
        {showResults && searchResults.length > 0 && (
          <div 
            id="search-results"
            className="absolute top-full left-0 right-0 mt-2 rounded-xl border bg-popover p-2 shadow-lg"
            role="listbox"
            aria-label="Search results"
          >
            {searchResults.map(tool => (
              <SearchResultItem 
                key={tool.id} 
                tool={tool} 
                onClick={() => handleToolClick(tool)} 
              />
            ))}
          </div>
        )}
        
        {showResults && searchQuery && searchResults.length === 0 && (
          <div 
            className="absolute top-full left-0 right-0 mt-2 rounded-xl border bg-popover p-4 shadow-lg"
            role="status"
            aria-live="polite"
          >
            <p className="text-sm text-muted-foreground text-center">No tools found</p>
          </div>
        )}
      </div>

      {/* Theme Toggle */}
      <Button
        variant="ghost"
        size="icon"
        onClick={toggleTheme}
        className="h-9 w-9"
        aria-label={theme === 'light' ? 'Switch to dark mode' : 'Switch to light mode'}
        aria-pressed={theme === 'dark'}
      >
        {theme === 'light' ? (
          <Moon className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Sun className="h-4 w-4" aria-hidden="true" />
        )}
      </Button>
    </header>
  );
});
